import React, { useMemo, useState } from 'react';
import { t } from 'i18n';
import { styled, Button, Card, Col, Row, Container } from '@teko/ds-desktop';
import {
  BuildPcTabs,
  BuildPcSummary,
  BuildPcItem,
  CustomLink,
} from '@teko/ecommerce';
import { useBuildPCV2, useCartV2 } from 'contexts';
import { isEmptyObject } from '@teko/utils';
import { useRouter } from 'next/router';
import { useTenantConfig } from 'hooks';
import { BuildPcModal } from './BuildPcModal';
import { AddItemsProduct } from '@teko/cart/v2';
import { logger } from '@teko/log';
import { trackCustomEvent } from '@teko/tracking';

export const BuildPCPicker = () => {
  const router = useRouter();
  const {
    buildPCData,
    buildPCDetailProducts,
    changeActiveConfig,
    increaseHardwareQuantity,
    decreaseHardwareQuantity,
    setHardwareQuantity,
    estimatedAmount,
    removeHardware,
    updateCurrentConfig,
  } = useBuildPCV2();
  const { buildPcMapping } = useTenantConfig();
  const { onAddMultipleItems } = useCartV2();
  const [selectedCategory, setSelectedCategory] = useState('');

  const buildPcMappingKeys = useMemo(() => {
    return !!buildPcMapping && !isEmptyObject(buildPcMapping)
      ? Object.keys(buildPcMapping)
      : [];
  }, [buildPcMapping]);

  if (isEmptyObject(buildPcMapping) || !buildPcMapping) {
    return null;
  }

  const onAddConfigurationToCart = async (): Promise<void> => {
    try {
      const configKeys = Object.keys(buildPCData.currentConfig);
      const products: AddItemsProduct[] = [];
      for (let i = 0; i < configKeys.length; i += 1) {
        const key = configKeys[i];
        const productData = buildPCDetailProducts.products.find((product) => {
          return product.sku === buildPCData.currentConfig[key]?.sku;
        });
        const quantity = buildPCData.currentConfig[key]?.quantity;

        if (!!productData && !isEmptyObject(productData) && !!quantity)
          products.push({
            sku: productData.sku,
            quantity,
            promotionId: !!productData.selectPromotionId
              ? productData.selectPromotionId
              : undefined,
          });
      }
      await onAddMultipleItems(products);
    } catch (error) {
      logger.log(error);
    }
  };

  return (
    <Container>
      <Row marginTop="s16" justify="space-between">
        <Col span={8.5} paddingLeft="s8" paddingRight="s8">
          <BuildPcTabs
            lsActiveTab={buildPCData.activeIndex}
            onClickTab={(index) => {
              changeActiveConfig(index);
              trackCustomEvent('changeBuildPCConfig', {
                action: 'click',
                category: 'buildpc',
                value: index + 1,
              });
            }}
            onResetConfig={() => updateCurrentConfig({})}
          />
        </Col>
      </Row>
      <Row marginTop="s16">
        <Col span={8.5} paddingLeft="s8" paddingRight="s8">
          {buildPcMappingKeys.map((buildPcMappingKey, index) => {
            const selectedHardware =
              buildPCData.currentConfig[buildPcMappingKey];
            const productData = buildPCDetailProducts.products.find(
              (product) => {
                return product.sku === selectedHardware?.sku;
              },
            );

            return (
              <StyledBuildPcItem
                key={`${index}-${buildPcMappingKey}`}
                title={buildPcMapping[buildPcMappingKey].name}
                icon={buildPcMapping[buildPcMappingKey].icon}
                onIncrease={() => {
                  increaseHardwareQuantity(buildPcMappingKey);
                }}
                onDecrease={() => {
                  // remove hardware from config when quantity < 1
                  selectedHardware?.quantity === 1
                    ? removeHardware(buildPcMappingKey)
                    : decreaseHardwareQuantity(buildPcMappingKey);
                }}
                setQuantity={(value) => {
                  setHardwareQuantity(buildPcMappingKey, value);
                }}
                product={
                  !!productData && !isEmptyObject(productData)
                    ? productData
                    : undefined
                }
                actionText={productData ? t('Sửa') : t('Chọn')}
                onClick={() => {
                  setSelectedCategory(buildPcMappingKey);
                }}
                quantity={selectedHardware?.quantity}
                showDeleteButton
                onDelete={() => removeHardware(buildPcMappingKey)}
              />
            );
          })}
        </Col>
        <Col span={3.5}>
          <StyledBuildPcSummary>
            <BuildPcSummary
              total={estimatedAmount}
              selectedCount={Object.keys(buildPCData.currentConfig).length}
              onAddToCart={onAddConfigurationToCart}
              onBuy={(): void => {
                onAddConfigurationToCart().then(() => router.push('/cart'));
              }}
            />
            <Card marginTop="s16" padding="s16">
              <CustomLink
                targetType="_blank"
                targetPath="https://www.messenger.com/t/phongvuvietnam"
              >
                <Button variant="primary" block>
                  {t('Nhận tư vấn từ chuyên gia')}
                </Button>
              </CustomLink>
            </Card>
          </StyledBuildPcSummary>
        </Col>
      </Row>
      {!!selectedCategory && (
        <BuildPcModal
          visible={!!selectedCategory}
          onClose={() => setSelectedCategory('')}
          categoryName={selectedCategory}
          collectionSlug={buildPcMapping[selectedCategory]?.slug || ''}
        />
      )}
    </Container>
  );
};

const StyledBuildPcItem = styled(BuildPcItem)`
  margin-bottom: 1rem;
`;
const StyledBuildPcSummary = styled.div`
  position: sticky;
  top: 80px;
  margin-bottom: 1rem;
`;
